/**
 * =================================================================================================
 * Load more pagination
 * =================================================================================================
 *
 * Replaces the collection page's numbered pages with a button that appends the next page of
 * products in place. Loaded only by sections/main-collection.liquid, and only when that section's
 * "pagination_style" setting is "load_more"; every global hook below is installed only when the
 * page carries [data-load-more-status], the marker that same setting renders.
 *
 * The markup it drives comes from snippets/pagination-control.liquid and the collection section:
 *
 *   <load-more-button data-section-id>  containing  a[data-load-more-link][href]
 *   p.load-more-pagination__count[data-load-more-total][data-load-more-count-template]
 *   .product-grid[data-load-more-loaded-page]   - the DOM's own record of how deep it has loaded
 *   [data-load-more-status][role=status]        - a live region outside the ajax container
 *
 * Without JavaScript the button is an ordinary link to the next page, so the page still works.
 *
 * Three things make Back and Forward behave. Each appended page pushes {loadMore: true, page: N},
 * and the theme's own FilterContainer popstate handler is wrapped rather than replaced, so a filter
 * or sort entry still reaches it. When a popstate lands on one of our entries for the same listing,
 * the DOM's loaded depth is reconciled against it. And when the browser does not keep the page in
 * memory, a return to "?page=N" re-fetches the earlier pages, prepends them and restores the saved
 * scroll offset from sessionStorage.
 *
 * It dispatches on:load-more:appended on `document` once per appended page, as an extension point.
 */

(function loadMore() {
  // How long a stored scroll record is trusted before a returning shopper just gets the plain
  // single-page render. An internal bound on the bfcache-bypass fallback, not a merchant setting.
  var RESTORE_WINDOW_MS = 30 * 60 * 1000;
  var STORAGE_PREFIX = 'theme:load-more:';
  var GRID_SELECTOR = '.filters-adjacent.collection-listing .product-grid';

  /**
   * The URL path and query with `page` removed: which listing (collection, filters, sort) is being
   * viewed, independent of how much of it is loaded.
   * @param {string} url
   * @returns {string}
   */
  function listingKeyFromUrl(url) {
    var parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (error) {
      return url;
    }
    var params = new URLSearchParams(parsed.search);
    params.delete('page');
    if (typeof params.sort === 'function') params.sort();
    var qs = params.toString();
    return parsed.pathname + (qs ? '?' + qs : '');
  }

  function storageKey() {
    return STORAGE_PREFIX + listingKeyFromUrl(window.location.href);
  }

  function currentPageFromLocation() {
    var n = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return n > 1 ? n : 1;
  }

  function buildPageUrl(pageNumber) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', String(pageNumber));
    return window.location.pathname + '?' + params.toString();
  }

  function sectionUrl(pageUrl, sectionId) {
    return pageUrl + (pageUrl.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + sectionId;
  }

  /**
   * Overwrites "Showing X of Y" with the live grid count. The template carries %CURRENT% and
   * %TOTAL% placeholders, so no second translation is needed.
   * @param {Element} sectionRoot
   * @param {number} currentCount
   */
  function updateCountDisplay(sectionRoot, currentCount) {
    var countEl = sectionRoot.querySelector('.load-more-pagination__count');
    if (!countEl) return;
    var template = countEl.dataset.loadMoreCountTemplate;
    var total = countEl.dataset.loadMoreTotal;
    if (!template || typeof total === 'undefined') return;
    // split/join rather than String#replace with a plain string, which substitutes only the first
    // occurrence: a translation that repeats a placeholder would otherwise keep it as literal text.
    var text = template.split('%CURRENT%').join(String(currentCount));
    countEl.textContent = text.split('%TOTAL%').join(String(total));
  }

  /**
   * Fetches one page of the current listing and hands back its grid children and pagination row.
   * @param {number} page
   * @param {string} sectionId
   * @returns {Promise<object>} { nodes, paginationHtml }
   */
  function fetchPage(page, sectionId) {
    return fetch(sectionUrl(buildPageUrl(page), sectionId), { method: 'get' })
      .then(function checkStatus(response) {
        if (!response.ok) throw new Error('HTTP error! Status: ' + response.status);
        return response.text();
      })
      .then(function parse(html) {
        var template = document.createElement('template');
        template.innerHTML = html;
        var newGrid = template.content.querySelector(GRID_SELECTOR)
          || template.content.querySelector('.product-grid');
        if (!newGrid) throw new Error('load-more: product grid not found for page ' + page);
        var newPaginationRow = template.content.querySelector('.pagination-row');
        return {
          nodes: Array.from(newGrid.children),
          paginationHtml: newPaginationRow ? newPaginationRow.innerHTML : ''
        };
      });
  }

  function announceAppended(sectionId, nodes, extra) {
    var detail = { sectionId: sectionId, nodes: nodes };
    Object.keys(extra || {}).forEach(function copy(key) { detail[key] = extra[key]; });
    // Dispatched on `document` rather than from the button: by this point the button has been
    // replaced along with its parent's innerHTML, and an event from a detached node reaches nothing.
    document.dispatchEvent(new CustomEvent('on:load-more:appended', { bubbles: true, detail: detail }));
  }

  /**
   * After an append the shopper's focus (a keyboard user was on the button) has just left the
   * document with the replaced button. Move it to the first focusable element of what was appended,
   * or to the new button. preventScroll keeps .focus() from scrolling on top of the shopper's click.
   * @param {Array} appendedNodes
   * @param {Element} paginationRow
   */
  function focusAfterAppend(appendedNodes, paginationRow) {
    var target = null;
    if (appendedNodes && appendedNodes.length > 0) {
      var firstCard = appendedNodes[0];
      if (firstCard.querySelector) target = firstCard.querySelector('a, button');
      if (!target && typeof firstCard.matches === 'function' && firstCard.matches('a, button')) target = firstCard;
    }
    if (!target && paginationRow) {
      target = paginationRow.querySelector('[data-load-more-link]') || paginationRow.querySelector('a, button');
    }
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  // Which listing is currently rendered. Kept up to date by the pushState wrapper (ours and the
  // theme's alike) and by the popstate handler.
  var currentListingKey = listingKeyFromUrl(window.location.href);
  var popStateInstalled = false;

  /**
   * Monkey-patches history.pushState once, so any push - this file's or FilterContainer's - updates
   * currentListingKey. That is what lets the popstate wrapper tell a "page changed" entry of ours
   * apart from one whose filters have changed since it was pushed.
   */
  function wrapHistoryPushState() {
    if (window.history.pushState.loadMoreWrapped) return;
    var originalPushState = window.history.pushState.bind(window.history);
    var wrapped = function (state, title, url) {
      var result = originalPushState(state, title, url);
      // Recomputed from window.location, not the possibly-relative `url`: by the time pushState
      // returns, window.location already reflects the destination.
      currentListingKey = listingKeyFromUrl(window.location.href);
      return result;
    };
    wrapped.loadMoreWrapped = true;
    window.history.pushState = wrapped;
  }

  /**
   * Appends pages (loadedDepth + 1)..targetPage in order. Only the last response refreshes the
   * pagination row, because its button correctly targets the depth this has just established -
   * never something derived from the URL. No pushState: the browser has already navigated.
   * @param {Element} sectionRoot
   * @param {string} sectionId
   * @param {number} loadedDepth
   * @param {number} targetPage
   * @param {Function} onDone - called with true once every page appended, false on the first failure.
   */
  function appendLaterPages(sectionRoot, sectionId, loadedDepth, targetPage, onDone) {
    var grid = sectionRoot.querySelector(GRID_SELECTOR);
    var paginationRow = sectionRoot.querySelector('.pagination-row');
    if (!grid || !paginationRow || targetPage <= loadedDepth) {
      onDone(false);
      return;
    }

    var page = loadedDepth + 1;
    (function fetchNext() {
      if (page > targetPage) {
        onDone(true);
        return;
      }
      var isLastPage = page === targetPage;
      var thisPage = page;
      fetchPage(thisPage, sectionId).then(function append(result) {
        // If a filter or sort reload replaced this listing while the fetch was in flight, `grid` is
        // a detached node. Bail rather than mutate a subtree nobody sees.
        if (!grid.isConnected) {
          onDone(false);
          return;
        }
        result.nodes.forEach(function add(node) { grid.appendChild(node); });
        grid.setAttribute('data-load-more-loaded-page', String(thisPage));
        announceAppended(sectionId, result.nodes, { reconciledPage: thisPage });
        if (isLastPage) {
          paginationRow.innerHTML = result.paginationHtml;
          updateCountDisplay(sectionRoot, grid.children.length);
        }
        page += 1;
        fetchNext();
      }).catch(function fail(error) {
        console.warn(error); // eslint-disable-line no-console
        onDone(false);
      });
    }());
  }

  /**
   * Fetches pages 1..uptoPage and prepends each ahead of whatever the server rendered. The
   * insertion anchor never moves, so inserting in page order builds the grid up correctly.
   * @param {Element} sectionRoot
   * @param {string} sectionId
   * @param {number} uptoPage
   * @param {Function} onDone
   */
  function prependEarlierPages(sectionRoot, sectionId, uptoPage, onDone) {
    var grid = sectionRoot.querySelector(GRID_SELECTOR);
    if (!grid || uptoPage < 1) {
      onDone(false);
      return;
    }

    var anchor = grid.firstElementChild;
    var page = 1;
    (function fetchNext() {
      if (page > uptoPage) {
        onDone(true);
        return;
      }
      var thisPage = page;
      fetchPage(thisPage, sectionId).then(function prepend(result) {
        if (!grid.isConnected) {
          onDone(false);
          return;
        }
        var fragment = document.createDocumentFragment();
        result.nodes.forEach(function add(node) { fragment.appendChild(node); });
        grid.insertBefore(fragment, anchor);
        announceAppended(sectionId, result.nodes, { restoredPage: thisPage });
        page += 1;
        fetchNext();
      }).catch(function fail(error) {
        console.warn(error); // eslint-disable-line no-console
        onDone(false);
      });
    }());
  }

  // True while any of the three fetch chains - a Load more click, a reconciliation, a restore - is
  // running. They all mutate the same grid and write the same count, so only one may run at a time.
  var fetchInFlight = false;

  /**
   * Runs after a fetch chain settles successfully. The history entry can have moved on while it was
   * fetching - a second Forward, say, which was ignored at the time - so the grid is reconciled once
   * more against the entry the browser is actually on. Reconciling to a depth already reached does
   * no fetch, so this ends after at most one further round.
   */
  function catchUpWithHistory() {
    var state = window.history.state;
    if (state && state.loadMore && typeof state.page === 'number') reconcileLoadedDepth(state.page);
  }

  /**
   * Reconciles the DOM's loaded depth against the page a load-more history entry expects.
   *
   *  - shallower than the entry (an intervening Back landed on a theme entry, whose full replace
   *    reset the grid to page 1, and Forward is returning to a deeper entry): backfill.
   *  - at or deeper than the entry: leave the products alone. Extra loaded products are not a loss,
   *    and the button on screen came from the last real fetch, so it already targets the real depth
   *    plus one; rebuilding it from this shallower entry would point it at a page already loaded.
   *    Only the count is refreshed, so it matches what is actually shown.
   *
   * Two Forward gestures in quick succession would otherwise both read the same loaded depth, fetch
   * the same page and append it twice. The rule is the simplest one that cannot duplicate: while a
   * fetch chain is in flight every further request is ignored, and the chain catches up with the
   * history entry once it settles.
   * @param {number} targetPage
   */
  function reconcileLoadedDepth(targetPage) {
    if (fetchInFlight) return;

    var statusEl = document.querySelector('[data-load-more-status]');
    if (!statusEl) return;

    var sectionId = statusEl.dataset.sectionId;
    var sectionRoot = statusEl.closest('.shopify-section') || document;
    var grid = sectionRoot.querySelector(GRID_SELECTOR);
    if (!sectionId || !grid) return;

    var loadedDepth = parseInt(grid.dataset.loadMoreLoadedPage, 10);
    if (!(loadedDepth > 0)) loadedDepth = 1;
    var target = typeof targetPage === 'number' && targetPage > 0 ? targetPage : loadedDepth;

    if (target <= loadedDepth) {
      updateCountDisplay(sectionRoot, grid.children.length);
      return;
    }

    fetchInFlight = true;
    statusEl.textContent = theme.strings.loadMoreLoading;
    appendLaterPages(sectionRoot, sectionId, loadedDepth, target, function done(success) {
      fetchInFlight = false;
      statusEl.textContent = success ? '' : theme.strings.loadMoreError;
      // Only on success: retrying a failed chain from here would loop on a listing that keeps
      // failing, and the message above is what tells the shopper.
      if (success) catchUpWithHistory();
    });
  }

  /**
   * Installs the popstate handler that keeps the grid in step with Back and Forward. An entry of
   * ours, for a listing that has not changed since it was pushed, is reconciled here; native scroll
   * restoration handles the scroll position itself.
   *
   * Where the theme's own ajax filtering is on, its FilterContainer listener is wrapped - never
   * removed - so its filter and sort entries still reach it, and correctness of the listing wins
   * over load-more's scroll position for anything that is not ours. Where ajax filtering is off
   * there is no listener to wrap and the same handler is installed on its own: this file pushes
   * {loadMore, page} entries in both configurations, so Back and Forward have to be answered in
   * both. Nothing else runs for an entry that is not ours, because without ajax filtering every
   * other history entry is a real navigation.
   */
  function installPopStateHandler() {
    if (popStateInstalled) return;
    popStateInstalled = true;

    var filterContainer = document.querySelector('filter-container[data-ajax-filtering="true"]');
    // main.js is deferred and comes first in document order, so FilterContainer has connected and
    // bound its handler by the time this runs.
    var originalHandler = (filterContainer && filterContainer.boundAjaxPopState) || null;
    if (originalHandler) window.removeEventListener('popstate', originalHandler);

    var handler = function (event) {
      var destinationKey = listingKeyFromUrl(window.location.href);
      var sameListing = destinationKey === currentListingKey;
      currentListingKey = destinationKey;

      if (event.state && event.state.loadMore && sameListing) {
        reconcileLoadedDepth(event.state.page);
        return;
      }
      if (originalHandler) {
        originalHandler(event);
        return;
      }
      // Standalone only. Without ajax filtering every entry that is not one of ours is a real
      // navigation, so the server render at this URL is the truth - and the grid on screen can be
      // several pages deeper than it, which is what Back from a load-more entry to the listing's
      // own first entry means. Reload rather than leave page 2's products under the page 1 URL.
      if (sameListing) window.location.reload();
    };

    if (filterContainer) {
      filterContainer.loadMoreWrappedPopState = handler;
      filterContainer.loadMoreOriginalPopState = originalHandler;
    }
    window.addEventListener('popstate', handler);
  }

  function persistScrollStateOnPageHide() {
    window.addEventListener('pagehide', function store() {
      var n = currentPageFromLocation();
      if (n <= 1) return; // nothing was ever loaded on this listing
      try {
        window.sessionStorage.setItem(storageKey(), JSON.stringify({ page: n, scrollY: window.scrollY, ts: Date.now() }));
      } catch (error) {
        // sessionStorage unavailable (private browsing, quota): the return visit simply has no
        // record to restore from.
      }
    });
  }

  /**
   * On a full (non-bfcache) return to "?page=N", re-fetches pages 1..N-1, prepends them and scrolls
   * back to where the shopper was.
   */
  function restoreScrollState() {
    var statusEl = document.querySelector('[data-load-more-status]');
    if (!statusEl) return;

    var sectionId = statusEl.dataset.sectionId;
    var n = currentPageFromLocation();
    if (n <= 1 || !sectionId) return;

    var record;
    try {
      record = JSON.parse(window.sessionStorage.getItem(storageKey()));
    } catch (error) {
      return; // unavailable or unreadable: degrade to the plain single-page render
    }
    if (!record || typeof record.page !== 'number' || typeof record.ts !== 'number') return;
    if (record.page < n) return; // the record does not cover this page
    if (Date.now() - record.ts > RESTORE_WINDOW_MS) return; // stale: treat as a fresh visit

    var uptoPage = Math.min(n - 1, record.page - 1);
    if (uptoPage < 1) return;

    var sectionRoot = statusEl.closest('.shopify-section') || document;
    statusEl.textContent = theme.strings.loadMoreLoading;
    // The restore chain holds the same lock: a Back or Forward landing while it is prepending must
    // not append to the grid it is still building.
    fetchInFlight = true;

    prependEarlierPages(sectionRoot, sectionId, uptoPage, function done(success) {
      fetchInFlight = false;
      if (!success) {
        statusEl.textContent = theme.strings.loadMoreError;
        return;
      }
      statusEl.textContent = '';

      var grid = sectionRoot.querySelector(GRID_SELECTOR);
      if (grid) updateCountDisplay(sectionRoot, grid.children.length);

      // Two frames, so the prepended cards have laid out before the scroll. The theme reserves
      // image space with --aspect-ratio, so there is no further shift to wait out.
      var targetScrollY = typeof record.scrollY === 'number' ? record.scrollY : 0;
      requestAnimationFrame(function firstFrame() {
        requestAnimationFrame(function secondFrame() {
          window.scrollTo(0, targetScrollY);
        });
      });

      catchUpWithHistory();
    });
  }

  // The one guard for every global hook above: [data-load-more-status] is rendered only by a
  // collection section whose pagination style is "load_more", so its presence is the signal that
  // any of this applies to the page at all.
  if (document.querySelector('[data-load-more-status]')) {
    wrapHistoryPushState();
    persistScrollStateOnPageHide();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function ready() {
        installPopStateHandler();
        restoreScrollState();
      });
    } else {
      installPopStateHandler();
      restoreScrollState();
    }
  }

  var LoadMoreButton = class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.link = this.querySelector('[data-load-more-link]');
      if (!this.link) return;

      this.sectionRoot = this.closest('.shopify-section') || document;
      this.statusEl = this.sectionRoot.querySelector('[data-load-more-status]');
      this.loading = false;

      this.boundClick = this.handleClick.bind(this);
      this.link.addEventListener('click', this.boundClick);
    }

    disconnectedCallback() {
      if (this.link && this.boundClick) this.link.removeEventListener('click', this.boundClick);
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    handleClick(evt) {
      evt.preventDefault();
      // this.loading is this button's own guard; fetchInFlight covers a reconciliation or a restore
      // running at the same moment, which would be fetching the very page this click asks for.
      if (this.loading || fetchInFlight) return;

      var sectionId = this.dataset.sectionId;
      var nextUrl = this.link.getAttribute('href');
      var grid = this.sectionRoot.querySelector(GRID_SELECTOR);
      var paginationRow = this.sectionRoot.querySelector('.pagination-row');
      // Which listing this response will belong to, fixed at click time and compared with
      // currentListingKey when it lands.
      var responseListingKey = listingKeyFromUrl(nextUrl);
      // The exact entry the shopper clicked from. A Back or Forward pressed while this fetch is in
      // flight is swallowed by the lock above, so the URL is re-read when the response lands: the
      // browser may have moved to another entry of this same listing, which the listing key alone
      // cannot see.
      var clickHref = window.location.href;

      if (!sectionId || !nextUrl || !grid || !paginationRow) {
        // Something this needs to append safely is missing (hand-edited markup, say): fall back to
        // a normal navigation rather than failing silently.
        window.location.href = nextUrl || this.link.href;
        return;
      }

      this.loading = true;
      fetchInFlight = true;
      this.link.setAttribute('aria-disabled', 'true');
      this.link.classList.add('is-loading');
      this.setStatus(theme.strings.loadMoreLoading);

      fetch(sectionUrl(nextUrl, sectionId), { method: 'get' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP error! Status: ' + response.status);
          return response.text();
        })
        .then((html) => {
          fetchInFlight = false;
          // A filter or sort reload can land while this fetch is in flight. It swaps in a fresh,
          // correctly filtered grid and pagination row and pushes its own history entry (caught by
          // the pushState wrapper), so `grid` is then a detached node. Appending to that is
          // harmless, but going on to overwrite `paginationRow` - the same, still-connected node,
          // only its innerHTML replaced - would stamp the new listing with this response's count
          // and next-page button. The listing key catches that; grid.isConnected additionally
          // catches the window where the key has changed but the DOM has not yet been replaced.
          if (!grid.isConnected || responseListingKey !== currentListingKey) {
            // This button's "Loading" announcement belongs to a listing no longer on screen.
            this.setStatus('');
            catchUpWithHistory();
            return;
          }

          var template = document.createElement('template');
          template.innerHTML = html;
          var newGrid = template.content.querySelector(GRID_SELECTOR)
            || template.content.querySelector('.product-grid');
          var newPaginationRow = template.content.querySelector('.pagination-row');
          if (!newGrid) throw new Error('load-more: product grid not found in the fetched page');

          var appended = Array.from(newGrid.children);
          appended.forEach((node) => grid.appendChild(node));

          // The DOM's own record of how deep this has gone, read later by reconcileLoadedDepth and
          // by the next click - never taken from the URL. Prefer the page just fetched; fall back
          // to one more than before only if that URL carried no page parameter.
          var previousDepth = parseInt(grid.dataset.loadMoreLoadedPage, 10);
          if (!(previousDepth > 0)) previousDepth = 1;
          var nextPageNumber = parseInt(new URL(nextUrl, window.location.origin).searchParams.get('page'), 10);
          if (!(nextPageNumber > 0)) nextPageNumber = previousDepth + 1;
          grid.setAttribute('data-load-more-loaded-page', String(nextPageNumber));

          paginationRow.innerHTML = newPaginationRow ? newPaginationRow.innerHTML : '';
          // The swapped-in row renders this page's own count; overwrite it with the live cumulative
          // grid count now that the products are actually in the grid.
          updateCountDisplay(this.sectionRoot, grid.children.length);

          // this.link no longer exists - its parent's innerHTML was just replaced.
          focusAfterAppend(appended, paginationRow);

          // Read before the push, which changes the URL itself.
          var stillOnClickEntry = window.location.href === clickHref;
          if (stillOnClickEntry) {
            // Advance the URL so it reflects how much is loaded. {loadMore, page} is what the
            // popstate handler reads on Back and Forward.
            window.history.pushState({ loadMore: true, page: nextPageNumber }, '', nextUrl);
          }

          this.setStatus('');
          announceAppended(sectionId, appended);

          // The shopper pressed Back or Forward while this was fetching. Pushing would have stacked
          // a new entry on top of the destination one and broken Forward, so nothing was pushed;
          // the products stay (a deeper DOM is allowed, and the row just swapped in matches that
          // depth) and the grid is reconciled against the entry the browser is actually on.
          if (!stillOnClickEntry) catchUpWithHistory();
        })
        .catch((error) => {
          console.warn(error); // eslint-disable-line no-console
          fetchInFlight = false;
          this.loading = false;
          this.link.removeAttribute('aria-disabled');
          this.link.classList.remove('is-loading');
          this.setStatus(theme.strings.loadMoreError);
          if (window.location.href !== clickHref) catchUpWithHistory();
        });
    }
  };

  window.customElements.define('load-more-button', LoadMoreButton);
}());
